import { Data, Effect } from 'effect';
import HomeIcon from '@listening/assets/icons/home.svg?react';
import {
  continueListeningArgs,
  libraryListInfiniteArgs,
  processingQueueInfiniteArgs,
} from '@listening/frontend/query/audio-list';
import { userSummaryArgs } from '@listening/frontend/query/user-hooks';
import { eq } from '@/runtimes/effect-query';
import { getRuntime } from '@/runtimes/get-runtime';
import { createFileRoute } from '@tanstack/react-router';
import { DashboardQueryError } from '@/components/shared-ui/dashboard-query-error';
import * as m from '@/paraglide/messages';
import { HomeV2 } from '@/components/web-fe/design-v2/home-v2';
import { DashboardPage } from '@/components/web-fe/pages/dashboard/dashboard-page';
import { DashboardContentSkeleton } from '@/components/web-fe/pages/dashboard/dashboard-skeleton';

// Best-effort prefetch failures are captured (never thrown) — see the loader
// comment below — so the specific cause doesn't matter beyond being typed.
class HomePrefetchError extends Data.TaggedError('HomePrefetchError')<{
  cause: unknown;
}> {}

export const Route = createFileRoute('/app/_authenticated/_dashboard/home')({
  component: HomePage,
  // Route-level skeleton so the home shell (upload row + "Your files" header +
  // card grid) shows during the loader wait, instead of the generic dashboard
  // skeleton — mirrors the sibling files/$folderId route.
  pendingComponent: HomePageSkeleton,
  pendingMs: 0,
  // Typed-error surface for this route's suspense reads (unwraps
  // `EffectQueryFailure`, auto-resets on navigation, retries via
  // `router.invalidate()`); renders at the dashboard Outlet so the chrome stays.
  errorComponent: DashboardQueryError,
  // Prefetch the bounded lists the home views read so the suspense reads below
  // resolve warm. Library + processing are infinite (first page only);
  // continue-listening is a single bounded page. User summary feeds the
  // subscription gates (premium file-type dialog, upload limit) — prefetching
  // it here means the dropzone's gate decision is warm before it's interactive.
  //
  // Best-effort: a prefetch failure must NOT reject the loader and bubble to the
  // root catch boundary — the suspense reads below re-run and surface a scoped,
  // typed error via this route's `errorComponent` instead. `mode: 'result'` runs
  // all of them to completion (no short-circuit) and captures each failure in the
  // success channel as a Result, so the composed effect never fails.
  loader: ({ context: { queryClient } }) =>
    getRuntime().runPromise(
      Effect.all(
        [
          Effect.tryPromise({
            try: () =>
              queryClient.infiniteQuery({
                ...eq.infiniteQueryOptions(libraryListInfiniteArgs()),
                staleTime: 'static',
              }),
            catch: (cause) => new HomePrefetchError({ cause }),
          }),
          Effect.tryPromise({
            try: () =>
              queryClient.infiniteQuery({
                ...eq.infiniteQueryOptions(processingQueueInfiniteArgs),
                staleTime: 'static',
              }),
            catch: (cause) => new HomePrefetchError({ cause }),
          }),
          Effect.tryPromise({
            try: () =>
              queryClient.query({
                ...eq.queryOptions(continueListeningArgs),
                staleTime: 'static',
              }),
            catch: (cause) => new HomePrefetchError({ cause }),
          }),
          Effect.tryPromise({
            try: () =>
              queryClient.query({
                ...eq.queryOptions(userSummaryArgs),
                staleTime: 'static',
              }),
            catch: (cause) => new HomePrefetchError({ cause }),
          }),
        ],
        { concurrency: 'unbounded', mode: 'result' },
      ).pipe(Effect.asVoid),
    ),
  context: () => ({
    title: m.common_home(),
    icon: HomeIcon,
  }),
});

function HomePage() {
  return (
    <DashboardPage>
      <HomeV2 />
    </DashboardPage>
  );
}

// Route pendingComponent: the static page shell. Renders the dashboard content
// skeleton ("Your library" header + card grid) inside the real
// `DashboardPage` so the home layout — with sidebar + top bar — is visible during
// the loader wait. The same `DashboardContentSkeleton` backs the pre-providers
// `DashboardSkeleton` shell, so the card grid is stable across that handoff.
function HomePageSkeleton() {
  return (
    <DashboardPage>
      <DashboardContentSkeleton />
    </DashboardPage>
  );
}
